import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    active: 'all',
    page: 1
  });
  const [pagination, setPagination] = useState({});

  const categories = [
    { id: 'all', name: 'All Categories', icon: 'fas fa-th' },
    { id: 'admission', name: 'Admission', icon: 'fas fa-bullhorn' },
    { id: 'examination', name: 'Examination', icon: 'fas fa-file-alt' },
    { id: 'event', name: 'Events', icon: 'fas fa-calendar' },
    { id: 'scholarship', name: 'Scholarship', icon: 'fas fa-award' },
    { id: 'placement', name: 'Placement', icon: 'fas fa-building' },
    { id: 'fee', name: 'Fee Notice', icon: 'fas fa-exclamation-triangle' },
    { id: 'result', name: 'Results', icon: 'fas fa-graduation-cap' },
    { id: 'workshop', name: 'Workshops', icon: 'fas fa-robot' },
    { id: 'holiday', name: 'Holidays', icon: 'fas fa-umbrella-beach' },
    { id: 'sports', name: 'Sports', icon: 'fas fa-trophy' }
  ];

  useEffect(() => {
    fetchAnnouncements();
  }, [filters]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        limit: 10
      };
      const response = await apiService.getAnnouncements(params);
      setAnnouncements(response.data.announcements);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setError('Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      try {
        await apiService.deleteAnnouncement(id);
        setAnnouncements(announcements.filter(ann => ann.id !== id));
      } catch (error) {
        console.error('Error deleting announcement:', error);
        setError('Failed to delete announcement');
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await apiService.toggleAnnouncementStatus(id);
      setAnnouncements(announcements.map(ann => 
        ann.id === id ? response.data.announcement : ann
      ));
    } catch (error) {
      console.error('Error toggling status:', error);
      setError('Failed to toggle status');
    }
  };

  const handleTogglePin = async (id) => {
    try {
      const response = await apiService.toggleAnnouncementPin(id);
      setAnnouncements(announcements.map(ann => 
        ann.id === id ? response.data.announcement : ann
      ));
    } catch (error) {
      console.error('Error toggling pin:', error);
      setError('Failed to toggle pin status');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="font-sans">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-sans font-bold text-slate">Announcements Management</h1>
        <Link 
          to="/admin/announcements/new" 
          className="px-4 py-2 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 flex items-center gap-2"
        >
          <i className="fas fa-plus"></i>
          New Announcement
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-slate mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate mb-2">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value, page: 1})}
              className="w-full px-3 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate mb-2">Status</label>
            <select
              value={filters.active}
              onChange={(e) => setFilters({...filters, active: e.target.value, page: 1})}
              className="w-full px-3 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
            >
              <option value="all">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Announcements List */}
      <div className="bg-white rounded-lg border border-border_gray shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-slate">
              <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
              Loading announcements...
            </div>
          </div>
        ) : announcements.length === 0 ? (
          <div className="text-center py-12">
            <i className="fas fa-bullhorn text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No announcements found</h3>
            <p className="text-gray-500">Create your first announcement to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Announcement</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Published</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {announcements.map((announcement) => (
                  <tr key={announcement.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-start">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 text-${announcement.color}`} style={{backgroundColor: `var(--${announcement.color}-light, #f3f4f6)`}}>
                          <i className={announcement.icon}></i>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-medium text-slate">{announcement.title}</h3>
                            {announcement.isPinned && (
                              <i className="fas fa-thumbtack text-crimson" title="Pinned"></i>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{announcement.description}</p>
                          <div className="text-xs text-gray-400 mt-1">
                            Views: {announcement.views}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                        {announcement.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPriorityColor(announcement.priority)}`}>
                        {announcement.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        announcement.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {announcement.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(announcement.publishedAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/admin/announcements/edit/${announcement.id}`}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </Link>
                        <button
                          onClick={() => handleTogglePin(announcement.id)}
                          className={`text-sm ${announcement.isPinned ? 'text-crimson' : 'text-gray-400'} hover:text-crimson`}
                          title={announcement.isPinned ? 'Unpin' : 'Pin'}
                        >
                          <i className="fas fa-thumbtack"></i>
                        </button>
                        <button
                          onClick={() => handleToggleStatus(announcement.id)}
                          className="text-yellow-600 hover:text-yellow-800 text-sm"
                          title={announcement.isActive ? 'Deactivate' : 'Activate'}
                        >
                          <i className={`fas ${announcement.isActive ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </button>
                        <button
                          onClick={() => handleDeleteAnnouncement(announcement.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing page {pagination.current} of {pagination.total} ({pagination.totalItems} total)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters({...filters, page: filters.page - 1})}
                disabled={pagination.current === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({...filters, page: filters.page + 1})}
                disabled={pagination.current === pagination.total}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Announcements;